/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9: every workout for today, from the member's programs and their
 * visit card, grouped by program. Pure, so each rule is testable.
 */
import type {
  LinkedProgrammingRef,
  TodaysWorkoutMembershipSource,
  WorkoutData,
} from "@lemonadetraining/contracts";
import { isKnownZone, tzOffsetMs } from "./gymTime";

/**
 * The `timezoneOffset` the today's-workout callables expect: minutes to add
 * to local time to reach UTC, as `Date.prototype.getTimezoneOffset` returns
 * (Helsinki in summer is -180). The gym's zone when it has a usable one, so
 * a member travelling abroad still sees their gym's day.
 */
export const todayOffsetMinutes = (now: Date, timeZone?: string | null): number => {
  if (timeZone && isKnownZone(timeZone)) return -Math.round(tzOffsetMs(now, timeZone) / 60000);
  return now.getTimezoneOffset();
};

interface RawCard {
  id?: string;
  UID?: string;
  programmingUID?: string;
  /** userTodaysWorkout marks a card the caller already recorded a result for. */
  hasRecordForThisWorkout?: boolean;
  workoutData?: Partial<WorkoutData>;
}

/** One entry of `userTodaysWorkout`: a program and its cards for today. */
export interface RawTodayProgramming {
  id?: string;
  name?: string;
  data?: RawCard[];
  /** The membership that granted it; absent on own programmes and from older functions. */
  membership?: TodaysWorkoutMembershipSource;
}

/** `getVisitCardTodaysWorkout`: every program of the first visit card with a workout today. */
export interface RawVisitCardToday {
  workouts?: RawCard[];
  /** The first contributing program's name; all an older function sends. */
  programmingName?: string | null;
  /** Each contributing program in link order; absent from older functions. */
  programmings?: LinkedProgrammingRef[];
  boxName?: string | null;
}

export interface TodayWorkout {
  id: string;
  programmingUID: string;
  workoutData: WorkoutData;
  /** The member already recorded a result for it. */
  hasResult: boolean;
}

export interface TodayProgram {
  id: string;
  name: string;
  /** How the member gets this program, when it is not their own subscription. */
  source: string | null;
  workouts: TodayWorkout[];
}

const toWorkout = (card: RawCard, programmingUID: string): TodayWorkout | null => {
  const id = card.UID ?? card.id;
  if (!id) return null;
  const data = card.workoutData ?? {};
  return {
    id,
    programmingUID,
    hasResult: card.hasRecordForThisWorkout === true,
    workoutData: {
      ...data,
      title: data.title ?? "",
      description: data.description ?? "",
      tags: data.tags ?? [],
      youTubeIDs: data.youTubeIDs ?? [],
      movements: data.movements ?? [],
    } as WorkoutData,
  };
};

/** A workout with a title, description, video or movement; a blank card is not a workout. */
const hasContent = (w: TodayWorkout): boolean => {
  const d = w.workoutData;
  return Boolean(
    d.title?.trim() ||
    d.description?.trim() ||
    (d.youTubeIDs ?? []).some((id) => typeof id === "string" && id !== "") ||
    (d.movements ?? []).length > 0
  );
};

const atGym = (what: string, gym: string | null | undefined) => {
  const name = gym?.trim();
  return name ? `With your ${what} at ${name}` : `With your ${what}`;
};

/** "With your visit card at Box", or "With your Unlimited at Box" for a recurring membership. */
export const sourceLabel = (m: TodaysWorkoutMembershipSource): string =>
  atGym(
    m.membershipType === "visit_card" ? "visit card" : m.name?.trim() || "membership",
    m.boxName
  );

/**
 * Programs in the order the server lists them, then the visit card's programs
 * in link order, each workout once. A text-only workout counts; only a day
 * with nothing is empty.
 */
export const groupTodayWorkouts = (
  programmings: RawTodayProgramming[],
  visitCard: RawVisitCardToday | null
): TodayProgram[] => {
  const seen = new Set<string>();
  const programs: TodayProgram[] = [];
  const take = (cards: RawCard[] | undefined, programmingUID: string) => {
    const out: TodayWorkout[] = [];
    for (const card of cards ?? []) {
      const workout = toWorkout(card, card.programmingUID ?? programmingUID);
      if (!workout || seen.has(workout.id) || !hasContent(workout)) continue;
      seen.add(workout.id);
      out.push(workout);
    }
    return out;
  };

  for (const prog of programmings) {
    if (!prog?.id) continue;
    const workouts = take(prog.data, prog.id);
    if (workouts.length > 0) {
      programs.push({
        id: prog.id,
        name: prog.name?.trim() || "Program",
        source: prog.membership ? sourceLabel(prog.membership) : null,
        workouts,
      });
    }
  }

  const visitCards = visitCard?.workouts ?? [];
  const fallbackID = visitCards.find((c) => c.programmingUID)?.programmingUID ?? "visit-card";
  const byProgram = new Map<string, RawCard[]>();
  for (const card of visitCards) {
    const uid = card.programmingUID ?? fallbackID;
    byProgram.set(uid, [...(byProgram.get(uid) ?? []), card]);
  }
  const refs = (Array.isArray(visitCard?.programmings) ? visitCard.programmings : []).filter(
    (ref) => typeof ref?.uid === "string" && ref.uid !== ""
  );
  const listed = refs.map((ref) => ref.uid).filter((uid) => byProgram.has(uid));
  const order = [...listed, ...[...byProgram.keys()].filter((uid) => !listed.includes(uid))];
  const source = atGym("visit card", visitCard?.boxName);
  const legacyName = visitCard?.programmingName?.trim();
  for (const uid of order) {
    const workouts = take(byProgram.get(uid), uid);
    if (workouts.length === 0) continue;
    // programmingName is the first program's raw name; the list fills a nameless one with a placeholder.
    const listedName =
      uid === refs[0]?.uid ? "" : refs.find((ref) => ref.uid === uid)?.name?.trim();
    const name = listedName || legacyName || "Today's workout";
    const own = programs.find((program) => program.id === uid);
    if (own) own.workouts.push(...workouts);
    else programs.push({ id: uid, name, source, workouts });
  }
  return programs;
};
